import {BlockChangeType, ChangedItem, RolandStructuredNamedObject, RolandStructuredObject} from "./RolandStructuredObject.js";
import {RolandStructuredVariableArray} from "./RolandStructuredArray.js";
import {MCUSD} from "./MC-VL/MCUSD.js";
import {MCLPD} from "./MC-VL/MCLPD.js";
import {MCLPDItem} from "./MC-VL/MCLPD.js";
import { PropertyAccessor } from "./PropertyAccessor.js";
import log from 'loglevel';

/**
 * Represents a chunk header in a Roland structured file.
 * Contains metadata about a data chunk including its tag name, model, start position, and length.
 */
export class ChunkHeader {
    /** @type {string} The tag name identifying the chunk type */
    tagName;
    /** @type {string} The model identifier for the chunk */
    model;
    /** @type {number} The starting byte offset of the chunk data */
    start;
    /** @type {number} The length of the chunk data in bytes */
    length;
    /** @type {RolandStructuredObject} The structured object representation of this chunk */
    structuredObject;

    /**
     * Creates a new ChunkHeader instance.
     * @param {string} tagName - The tag name identifying the chunk type
     * @param {string} model - The model identifier for the chunk
     * @param {number} start - The starting byte offset of the chunk data
     * @param {number} length - The length of the chunk data in bytes
     */
    constructor(tagName, model, start, length) {
        this.tagName = tagName;
        this.model = model;
        this.start = start;
        this.length = length;
    }
}

export const FileType = Object.freeze({
    UNKNOWN_FILE_TYPE:'UnknownFileType',
    SVZ:'SVZa',
    CLIP:'Clip',
    MC_VL_PROJECT:'MCVLProject',
    SDZ:'SDZ',
    SVD:'SVD',
    SH_4D_PROJECT:'SH4DProject'
});

export const FileSubType = Object.freeze({
    NO_SUBTYPE:'NoSubtype',
    MC_PROJECT:'MCProject',
    VL_PROJECT:'VLProject',
    TONE_CLIP:'ToneClip',
    DRUM_CLIP:'DrumClip',
});

export const FileTypeHeader = Object.freeze({
    SVZ:0x53565A61,           // SVZa  
    MC_VL_PROJECT:0x50524A35, // PRJb
    TONE_CLIP:0x434C5054,     // CLPT
    DRUM_CLIP:0x434C5044,     // CLPD
    SVD:0x53564435,           // SVDa
});

export const FileModelCode = Object.freeze({
    MC_707_101:"MC-707/101",
    VL_1:"VL-1",
    SH_4D_JUPITER:"SH-4D/Jupiter-X",
    JUNO_X:"Juno-X",
    ZCORE:"Z Core",
    FANTOM:"Fantom",
});

/** reverse lookup of FileModelCode */
export const FileModelCodeLookup = Object.freeze({
    "MC77":FileModelCode.MC_707_101,
    "MV44":FileModelCode.VL_1,
    "JP-M":FileModelCode.SH_4D_JUPITER,
    "JU-X":FileModelCode.JUNO_X,
    "ZCOR":FileModelCode.ZCORE,
    "KY19":FileModelCode.FANTOM
});

/**
 * Represents a Roland structured file that contains multiple data chunks.
 * Extends RolandStructuredObject to provide file-level operations and chunk management.
 */
export class RolandStructuredFile extends RolandStructuredObject {
    /** @type {FileType|null} The type of the file */
    fileType = FileType.UNKNOWN_FILE_TYPE;
    /** @type {FileSubType|null} The subtype of the file */
    fileSubType = FileSubType.NO_SUBTYPE;
    /** @type {FileModelCode|null} The model code of the file (hardware model/ZCOR) */
    modelCode = null;
    /** @type {string|null} The name of the file */
    fileName = null;
    /** @type {number} The number of chunks in the file */
    numChunks = 0;
    /** @type {number} The starting offset of chunk headers */
    chunkHeaderStart = 16;
    /**
     * @type {ChunkHeader[]}
     */
    chunkHeaders = [];
    /**
     * @type {Map<string, ChunkHeader>}
     */
    chunkHeaderMap = new Map();

    /**
     * Don't call this directly, use the static function create
     * @param {File} file - The file object
     * @param {DataView} data - The data view containing the file content
     * @param {string} fileName - The name of the file
     */
    constructor(file, data, fileName ) {
        super(file, data, undefined, 0, data.byteLength);
        this.propertiesName = "FILE_HEADER";
        this.fileName = fileName;
    }

    /**
     * Create a RolandStructuredFile object (or a derived class) from databuffer depending on the file type.
     * 
     * @param buffer
     * @param fileName
     * @returns {RolandStructuredFile}
     */
    static createFromBuffer(buffer,fileName) {
        const view = new DataView(buffer);
        let file;
        let guessedHeaderType = RolandStructuredFile.guessHeaderType(view);
        if (guessedHeaderType==="A") {
            file = new RolandStructuredFileHeaderA(null, view, fileName );
        } else if (guessedHeaderType==="B") {
            file = new RolandStructuredFileHeaderB(null, view, fileName );
        } else {
            throw "Unknown file type";
        }

        file.load();

        return file;
    }

     /**
     * Guess the header type from the file content
     * @param {DataView} data - The data view containing the file content
     * @returns {string} The header type
     */
    static guessHeaderType(data) {
        if (data.getUint16(0,true) <= 255) {
            return "A";
        } else {
            let numChunks = data.getUint8(0x04);
            if (numChunks>=2 && numChunks<=30) 
                return "B";                
        }
        return "unknown";
    }   

    /**
     * Calculate the number of chunks in the file, depends on the file type.
     * May adjust the chunkDataStart value.
     */
    calcNumChunks() {
        throw new Error("calcNumChunks pure virtual call");
    }

    load() {
        console.log(`Loading file: ${this.fileName}`);
        this.calcNumChunks();
        if (this.numChunks>10 || this.numChunks<2) {
            throw "Invalid file format";
        }

        this.ownedLength = this.chunkHeaderStart+this.numChunks*16;

        this.readChunkHeaders();
        this.determineFileType();
        this.readChunks();
    }

    /**
     * Load the object (and sub objects) from the view data
     * @override
     * @param {any} lc - Load context parameter
     * @returns {string[]} Array containing the file type
     */
    nestedLoad(lc) {
        
        this.readChunks(lc);
        
        return [this.fileType];
    }

    /**
     * Read the chunk header data in, these point to their data items
     */
    readChunkHeaders() {
        let offset=16;      // start at 16
        for(let i = 0; i < this.numChunks; i++) {
            let ch = new ChunkHeader( this.getString(offset,4),
                this.getString(offset+4,4),
                this.getUint32(offset+8),
                this.getUint32(offset+12) );

            if (ch.length>this.length || ch.start+ch.length>this.length)
                throw "Invalid file format";

            this.chunkHeaders.push( ch );
            this.chunkHeaderMap.set(ch.tagName,ch);
            offset+=16;
        }
    }

    /**
     * Determine the file type from the file content
     * @returns {string} The file type
     */
    determineFileType() {
        let id = this.getUint32(0,false);
        let id2 = this.getUint32(2,false);
        let modelCodeText = this.getString(this.chunkHeaderStart + 4, 4);
        this.modelCode = FileModelCodeLookup[modelCodeText];        
        
        if (id===0x53565A61) {      
            this.fileType = "SVZa";
        } else if ( id2==0x50524A35 ) {
            this.fileType = FileType.MC_VL_PROJECT;
            this.fileSubType = this.hasChunk("PRJb") ? FileSubType.VL_PROJECT : FileSubType.MC_PROJECT;
        } else if ( id2==0x434C5054 ) {
            this.fileType = FileType.CLIP;
            this.fileSubType = FileSubType.TONE_CLIP;
        } else if ( id2==0x434C5044 ) {
            this.fileType = FileType.CLIP;
            this.fileSubType = FileSubType.DRUM_CLIP;
        } else if ( id2==0x53564435 ) {
            this.fileType = (this.modelCode==FileModelCode.SH_4D_JUPITER && this.hasChunk("PATc")) ? FileType.SH_4D_PROJECT : FileType.SVD;
        } 
        console.log(`File type: ${this.fileType}, File sub type: ${this.fileSubType}`);
    }

    /**
     * Check if the file contains a chunk with the specified tag name
     * @param {string} tagName - The tag name to check for
     * @returns {boolean} True if the chunk exists, false otherwise
     */
    hasChunk(tagName) {
        return this.chunkHeaderMap.has(tagName);
    }

    /**
     * Get the first child of a hunk by its tag name
     * @param {string} tagName - The tag name of the chunk to retrieve
     * @returns {RolandStructuredArray} The chunk object, or undefined if not found
     */
    getChunkFirstChild(tagName) {
        const header = this.chunkHeaderMap.get(tagName);
        if (!header?.structuredObject?.children?.length) return undefined;
        return header.structuredObject.children[0];
    }

    /**
     * Get the raw chunk by its tag name. 99.99% of the time this will be a RolandStructuredVariableArray
     * @param {string} tagName - The tag name of the chunk to retrieve
     * @returns {RolandStructuredArray} The chunk object, or undefined if not found
     */
    getChunk(tagName) {
        const header = this.chunkHeaderMap.get(tagName);
        return header ? header.structuredObject : undefined;
    }

    /**
     * Breadcrumb-style access into the file. The first segment is a chunk tag
     * with an optional `[N]` selecting a child of the chunk's array (defaults
     * to 0). Further segments descend into that child's ZenProperties
     * structure. The final token returns either a `RolandStructuredObject`
     * (when the path stops at a file-tree child) or a `PropertyAccessor`
     * (when it descends into a ZenProperties sub-structure).
     *
     * Examples:
     *   file.at("PATa[0]")                        // the first ZencoreTone child
     *   file.at("PATa[0].PCMT_CMN")               // accessor into its PCMT_CMN block
     *   file.at("PATa[0].PCMT_CMN").getUint32("NAME_1")
     *
     * @param {string} path
     * @returns {RolandStructuredObject|import("./PropertyAccessor.js").PropertyAccessor}
     */
    at(path) {
        // If the file itself has a descriptor (propertiesName), allow `at("")`
        // as a shorthand to get its root PropertyAccessor. This is useful for
        // UIs that render any descriptor-backed object via `at("")`.
        if (!path) {
            if (this.propertiesName)
                return PropertyAccessor.fromObject(this, "");
            throw new Error("File.at(path) requires a chunk path");
        }

        const dot = path.indexOf('.');
        const head = dot < 0 ? path : path.substring(0, dot);
        const rest = dot < 0 ? ''   : path.substring(dot + 1);

        const m = head.match(/^([A-Za-z_0-9]+)(?:\[(\d+)\])?$/);
        if (!m) throw new Error(`Bad chunk path segment "${head}"`);

        const chunk = this.getChunk(m[1]);
        if (!chunk) {
            const available = [...this.chunkHeaderMap.keys()].sort().join(', ');
            throw new Error(`No chunk "${m[1]}" in this file. Available: ${available}`);
        }

        const idx = m[2] !== undefined ? parseInt(m[2], 10) : 0;
        const child = chunk.children[idx];
        if (!child) throw new Error(`No child ${idx} in chunk "${m[1]}"`);

        if (!rest) return child;
        return child.at(rest);
    }
    
    /**
     * Get the class for a chunk
     * @param {string} tagName 
     * @param {string} model 
     * @returns {Array} [ parentArrayClass, class, propertiesName ]
     */
    getChunkClass(tagName, model) {
        let rv=[ RolandStructuredVariableArray,RolandStructuredObject ];
        switch(tagName) {            
            // Basic Zencore tone/models
            case 'PCMa':
            case 'PATa':
                rv[1] = RolandStructuredNamedObject.fromDescriptor("PCMEX");
                break;
            case 'MDLa':
                rv[1] = RolandStructuredObject.fromDescriptor("PCMEX_MODEL");
                break;
            case 'PCIa':
            case 'INSa':
                rv[1] = RolandStructuredObject.fromDescriptor("ZEN_DRUMKIT");
                break;
            case 'PCRa':
            case 'RHYa':
                rv[1] = RolandStructuredNamedObject.fromDescriptor("PCMR");
                break;

            // ---- samples and looper
            case 'USDa':
                rv[1] = MCUSD;
                break;
            case 'USPa':
                break;
            case 'LPDa':
                rv[0] = MCLPD;
                rv[1] = MCLPDItem;
                break;
                
            // SH-4d project files
            case 'RHIa':
                rv[1] = RolandStructuredNamedObject.fromDescriptor("ZEN_DRUMKIT");
                break;
            case 'PATc':
                rv[1] = RolandStructuredObject.fromDescriptor("SH4D_PATCH");
                break;
            case 'PRFd':
                rv[1] = RolandStructuredObject.fromDescriptor("PATTERN_SH4D");
                break;
            
            // MC-VL project files
            case 'USRa':
                rv[1] = RolandStructuredNamedObject.fromDescriptor("MCUSR");
                break;
            case 'PRJa':
                rv[1] = RolandStructuredNamedObject.fromDescriptor("MCPRJ");
                break;

        }
        return rv;
    }

    /**
     * Read chunks and create child objects.
     * @param {any} lc - Load context parameter
     */
    readChunks(lc) {
        for(let ch of this.chunkHeaders) {
            let chunk = null;

            let [parentArrayClass, childClass ] = this.getChunkClass(ch.tagName, ch.model);

            chunk = new parentArrayClass(this,this.data,this,ch.start,ch.length,childClass);

            if (chunk!=null) {
                ch.structuredObject = chunk;
                chunk.description = ch.tagName+" Chunk";
                this.children.push(chunk);
                // DUMY is 0 length at end of file
                if (ch.length>0)
                    chunk.nestedLoad(lc);
            }
        }
    }

    /**
     * Compare this file with another RolandStructuredFile and record differences
     * @param {RolandStructuredFile} other - The other file to compare with
     * @param {ChangedItem[]} resultsArray - Array to store the comparison results
     */
    compareWith(other, resultsArray) {
       let differences = [];
       let additions = [];
       let removals = [];

       for( let [key,value] of other.chunkHeaderMap )
           if (!this.chunkHeaderMap.has(key))
               resultsArray.push( new ChangedItem(BlockChangeType.newItem,false,false,null,other));

       for( let [key,value] of this.chunkHeaderMap )
           if (!other.chunkHeaderMap.has(key))
               resultsArray.push( new ChangedItem(BlockChangeType.deletedItem,false,false,null,other));

       for( let [key,chunk] of this.chunkHeaderMap )
        if (other.chunkHeaderMap.has(key))
            this.chunkHeaderMap.get(key).structuredObject.compareWith(other.chunkHeaderMap.get(key).structuredObject, resultsArray);
    }

    /**
     * Prepare the output data view for writing the file
     * @return {DataView} The prepared data view
     */
    prepareOutputDataView( ) {
        let returnedLength = this.calculateLength(0);
        console.log(`File length After Calculate Length: ${returnedLength}`);

        // update chunkHeaders with right size and length;
        let address = 24;      // first chunk address point. 16 bytes head + 8 bytes tag.
        this.children.forEach( (chunk) => {
            this.setInt32(address, chunk.calculatedNewPosition);
            this.setInt32(address +4, chunk.length);
            address+=16;
        });

        let dv = new DataView(new ArrayBuffer(this.length));

        console.log(`DataView length After Calculate Length: ${this.length}`);
        this.writeDataToView(0, dv);

        return dv;
    }

    /**
     * Dump information about all child objects to the console
     */
    dump() {
        this.children.forEach( (child) => {
            log.debug(`${child.constructor.name} ${child.description} ${child.start} ${child.start.toString(16)} ${child.length} ${child.length.toString(16)} `);
        })
    }

}

/**
 * Header type 'A'.
 * Chunk header start at 16 bytes
 * 16 bits LE at byte 0 is number of bytes (after this) to the start of chunk data.
 * numChunks = (this.getUint16(0) + 2 - 16) / 16;
 */
export class RolandStructuredFileHeaderA extends RolandStructuredFile {
    /**
     * Creates a new RolandStructuredFileHeaderA instance.
     * @param {File} file - The file object
     * @param {DataView} data - The data view containing the file content
     * @param {string} fileName - The name of the file
     */
    constructor(file, data, fileName ) {
        super(file, data, fileName);
    }

    /**
     * Calculate the number of chunks in the file
     */
    calcNumChunks() {
        this.numChunks = (this.getUint16(0) + 2 - 16) / 16;
    }

}

/**
 * Header type 'B'.
 * Chunk header start at 16 bytes
 * byte at 0x04 is number of chunks
 */
export class RolandStructuredFileHeaderB extends RolandStructuredFile {
    /**
     * Creates a new RolandStructuredFileHeaderB instance.
     * @param {File} file - The file object
     * @param {DataView} data - The data view containing the file content
     * @param {string} fileName - The name of the file
     */
    constructor(file, data, fileName) {
        super(file, data, fileName );
    }

    calcNumChunks() {
        this.numChunks = this.getUint8(0x04)
       }

}
